import React, { useState, useEffect } from 'react';
import { init, hapticFeedback, miniApp, backButton } from './utils/telegramSdk';
import { BottomMenu } from './components/BottomMenu';
import { MainPage } from './components/MainPage';
import { PurchasePage } from './components/PurchasePage';
import { KeyDetailPage } from './components/KeyDetailPage';
import { WalletPage } from './components/WalletPage';
import { FaqPage } from './components/FaqPage';
import { ReferralPage } from './components/ReferralPage';
import { BlockedScreen } from './components/BlockedScreen';
import { MaintenanceScreen } from './components/MaintenanceScreen';
import { PolygonSpinner } from './components/PolygonSpinner';
import { SuccessSheet } from './components/SuccessSheet';
import { useTelegram } from './hooks/useTelegram';
import { usePlatform } from './hooks/usePlatform';
import './App.css';
import { LoadingSheet } from "./components/LoadingSheet";
import { InsufficientBalanceSheet } from "./components/InsufficientBalanceSheet";
import { SuccessRenewSheet } from "./components/SuccessRenewSheet";
import { AuthPage } from './components/AuthPage';
import { VerifyEmailPage } from "./components/VerifyEmailPage";
import { TelegramAuthHandler } from './components/TelegramAuthHandler';
import {ProfilePage} from "./components/ProfilePage";


function App() {
    const [currentPage, setCurrentPage] = useState('main');
    const [activeMenuItem, setActiveMenuItem] = useState('vpn');
    const [renewData, setRenewData] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);
    const [pageAnimation, setPageAnimation] = useState('enter');
    const [nextPage, setNextPage] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [maintenanceMessage, setMaintenanceMessage] = useState('');
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [error, setError] = useState(null);
    const [showLoadingSheet, setShowLoadingSheet] = useState(false);
    const [showSpinner, setShowSpinner] = useState(true);
    const [showSuccessSheet, setShowSuccessSheet] = useState(false);
    const [showSuccessRenewSheet, setShowSuccessRenewSheet] = useState(false);
    const [showBottomMenu, setShowBottomMenu] = useState(true);
    const [pendingPurchase, setPendingPurchase] = useState(null);
    const [showInsufficientBalanceSheet, setShowInsufficientBalanceSheet] = useState(false);
    const [newlyPurchasedKeyUuid, setNewlyPurchasedKeyUuid] = useState(null);
    const [activeTab, setActiveTab] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [webUser, setWebUser] = useState(null);
    const [telegramInitialized, setTelegramInitialized] = useState(false);
    const [internalUserId, setInternalUserId] = useState(null);

    const [insufficientBalanceData, setInsufficientBalanceData] = useState({
        required: 0,
        current: 0
    });
    const API_URL = window.location.origin;

    // Состояния для загрузки данных страниц
    const [mainPageDataLoaded, setMainPageDataLoaded] = useState(false);
    const [walletPageDataLoaded, setWalletPageDataLoaded] = useState(false);
    const [referralPageDataLoaded, setReferralPageDataLoaded] = useState(false);
    const [faqPageDataLoaded, setFaqPageDataLoaded] = useState(false);
    const [currentPageLoading, setCurrentPageLoading] = useState(true);

    const [isAdmin, setIsAdmin] = useState(false);
    const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);

    const [userKeys, setUserKeys] = useState([]);
    const { userId, telegramId, user, sendData, tg, isTelegram, isWeb } = useTelegram();
    const { platform } = usePlatform();

    useEffect(() => {
        const checkLocalStorageAuth = () => {
            // В Telegram источником личности является только текущий WebApp user.
            // Сохранённый internalUserId мог принадлежать другой веб-сессии.
            if (window.Telegram?.WebApp?.initDataUnsafe?.user?.id) {
                setInternalUserId(null);
                setIsAuthenticated(false);
                setWebUser(null);
                return;
            }

            const token = localStorage.getItem('token');
            const savedUser = localStorage.getItem('user');
            const savedInternalId = localStorage.getItem('internalUserId');

            console.log('🔍 App.jsx - проверка localStorage при загрузке:');
            console.log('  token:', token ? 'есть' : 'нет');
            console.log('  savedUser:', savedUser ? 'есть' : 'нет');
            console.log('  savedInternalId:', savedInternalId ? 'есть' : 'нет');

            if (token && savedUser) {
                try {
                    const user = JSON.parse(savedUser);
                    console.log('✅ Восстановлен пользователь из localStorage:', user);
                    setIsAuthenticated(true);
                    setWebUser(user);
                    if (savedInternalId) {
                        setInternalUserId(parseInt(savedInternalId));
                    }
                } catch (e) {
                    console.error('❌ Ошибка парсинга user из localStorage:', e);
                }
            } else {
                console.log('ℹ️ Нет данных в localStorage для восстановления');
            }
        };

        checkLocalStorageAuth();
    }, []);

    async function getInternalIdByTelegramId(telegramId) {
        try {
            const response = await fetch(`${API_URL}/api/user/get-internal-id/${telegramId}`);
            const data = await response.json();

            if (data.success) {
                console.log('Внутренний ID:', data.internalId);
                console.log('Данные пользователя:', data.user);
                return data.internalId;
            } else {
                console.error('Ошибка:', data.error);
                return null;
            }
        } catch (error) {
            console.error('Ошибка запроса:', error);
            return null;
        }
    }

    // Проверка авторизации через API (валидация токена)
    useEffect(() => {
        const validateToken = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;

            try {
                const response = await fetch(`${API_URL}/api/auth/verify`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();

                if (data.success) {
                    if (data.user.emailVerified === false) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        localStorage.removeItem('internalUserId');
                        setIsAuthenticated(false);
                        setError('Пожалуйста, подтвердите ваш email. Проверьте почту.');
                    } else {
                        setIsAuthenticated(true);
                        setWebUser(data.user);
                    }
                } else {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    localStorage.removeItem('internalUserId');
                    setIsAuthenticated(false);
                }
            } catch (error) {
                console.error('Ошибка валидации токена:', error);
            }
        };

        if (!isTelegram) {
            validateToken();
        }
    }, [isTelegram]);

    // Проверка статуса техработ
    useEffect(() => {
        const checkMaintenanceStatus = async () => {
            try {
                console.log('🔧 Проверка статуса техработ...');
                setMaintenanceMode(false);
            } catch (error) {
                console.error('❌ Ошибка:', error);
                setMaintenanceMode(false);
            }
        };
        checkMaintenanceStatus();
    }, []);

    const checkIsAdmin = async () => {
        try {
            const telegramId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
            if (!telegramId) {
                setIsCheckingAdmin(false);
                return;
            }
            const response = await fetch(`${API_URL}/api/user/is-admin?telegramId=${telegramId}`);
            const data = await response.json();
            if (data.success) {
                setIsAdmin(data.isAdmin);
                console.log(`👑 Статус администратора: ${data.isAdmin ? 'Да' : 'Нет'}`);
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setIsAdmin(false);
        } finally {
            setIsCheckingAdmin(false);
        }
    };

    useEffect(() => {
        if (userId) {
            checkIsAdmin();
        }
    }, [userId]);

    // После получения userId проверяем блокировку
    useEffect(() => {
        if (isAuthenticated && userId && !maintenanceMode) {
            console.log('✅ Пользователь авторизован, ID:', userId);
            checkBlockStatus();
        } else if (isAuthenticated && userId && maintenanceMode) {
            setCheckingStatus(false);
            setIsReady(true);
            setShowSpinner(false);
        }
    }, [isAuthenticated, userId, maintenanceMode]);

    // Загружаем ключи
    useEffect(() => {
        console.log('🔍 useEffect для загрузки ключей:', {
            isReady,
            isBlocked,
            maintenanceMode,
            internalUserId,
            isAuthenticated
        });

        if (isReady && !isBlocked && !maintenanceMode && internalUserId) {
            console.log('✅ Загружаем ключи для internalUserId:', internalUserId);
            fetchUserKeys();
        } else {
            console.log('⏳ Условия не выполнены для загрузки ключей');
        }
    }, [isReady, isBlocked, maintenanceMode, internalUserId]);

    // Смена страницы
    useEffect(() => {
        console.log('🔄 Смена страницы на:', currentPage);
        const timeoutId = setTimeout(() => {
            setCurrentPageLoading(false);
        }, 1000);
        return () => clearTimeout(timeoutId);
    }, [currentPage]);

    const fetchUserKeys = async () => {
        try {
            console.log('📡 Загрузка ключей...');
            const response = await fetch(`${API_URL}/api/user/${internalUserId}/keys`, { cache: 'no-store' });
            const data = await response.json();
            if (data.success) {
                setUserKeys(data.keys);
                setSelectedKey(previous => previous
                    ? data.keys.find(key => key.uuid === previous.uuid) || previous
                    : previous
                );
                return data.keys;
            } else {
                setError(data.error || 'Ошибка загрузки ключей');
                return [];
            }
        } catch (error) {
            console.error('❌ Ошибка:', error);
            setError(error.message);
        } finally {
            setMainPageDataLoaded(true);
            setTimeout(() => setShowSpinner(false), 1000);
        }
    };

    const handleCloseMenu = () => {
        setShowBottomMenu(false);
    };

    const handleOpenMenu = () => {
        setShowBottomMenu(true);
    };

    const handlePageDataLoaded = (pageName) => {
        console.log(`✅ Страница ${pageName} загружена`);
        switch (pageName) {
            case 'main':
                setMainPageDataLoaded(true);
                break;
            case 'wallet':
                setWalletPageDataLoaded(true);
                break;
            case 'referral':
                setReferralPageDataLoaded(true);
                break;
            case 'faq':
                setFaqPageDataLoaded(true);
                break;
            default:
                break;
        }
    };

    const handleAuthSuccess = async (userData) => {
        console.log('🎉 App.jsx - handleAuthSuccess вызван:', userData);
        console.log('🎉 userData.id:', userData?.id);
        console.log('🎉 userData.telegram_id:', userData?.telegram_id);

        // Если id нет, но есть telegram_id, используем его
        let userId = userData?.id;

        if (!userId && userData?.telegram_id) {
            // Для Telegram пользователей получаем внутренний ID
            userId = await getInternalIdByTelegramId(userData.telegram_id);
            console.log('⚠️ Получен внутренний ID через getInternalIdByTelegramId:', userId);
        }

        if (!userId) {
            console.error('❌ Нет ID пользователя!');
            return;
        }

        // Сохраняем в localStorage
        const userToSave = { ...userData, id: userId };
        localStorage.setItem('user', JSON.stringify(userToSave));
        localStorage.setItem('internalUserId', userId.toString());

        // Устанавливаем состояния
        setIsAuthenticated(true);
        setWebUser(userToSave);
        setInternalUserId(userId);

        // Устанавливаем isReady
        setCheckingStatus(false);
        setIsReady(true);

        // Загружаем ключи с небольшой задержкой для уверенности
        setTimeout(() => {
            if (userId) {
                console.log('🔄 Загрузка ключей после авторизации для userId:', userId);
                fetchUserKeysDirect(userId);
            }
        }, 500);
    };

    const checkBlockStatus = async () => {
        try {
            console.log('🔍 checkBlockStatus started...');

            // userId из useTelegram — внутренний users.id. Для регистрации
            // разрешено использовать только настоящий Telegram ID.
            const currentTelegramId = telegramId ||
                window.Telegram?.WebApp?.initDataUnsafe?.user?.id || null;

            if (!currentTelegramId) {
                console.error('❌ Не удалось получить Telegram ID');
                setCheckingStatus(false);
                setIsReady(true);
                setShowSpinner(false);
                return;
            }

            // Регистрируем пользователя
            const registerResponse = await fetch(`${API_URL}/api/user/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telegramId: currentTelegramId,
                    initData: window.Telegram?.WebApp?.initData || '',
                    userData: {
                        username: window.Telegram?.WebApp?.initDataUnsafe?.user?.username || user?.username,
                        first_name: window.Telegram?.WebApp?.initDataUnsafe?.user?.first_name || user?.first_name,
                        last_name: window.Telegram?.WebApp?.initDataUnsafe?.user?.last_name || user?.last_name,
                        language_code: window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code || 'ru',
                        is_premium: window.Telegram?.WebApp?.initDataUnsafe?.user?.is_premium ? 1 : 0
                    }
                })
            });

            if (!registerResponse.ok) {
                const errorData = await registerResponse.json().catch(() => ({}));
                console.error('❌ Ошибка регистрации:', registerResponse.status, errorData);
                setCheckingStatus(false);
                setIsReady(true);
                setShowSpinner(false);
                return;
            }

            const registerData = await registerResponse.json();
            console.log('📡 Ответ регистрации:', registerData);

            if (!registerData.success) {
                console.error('❌ Ошибка в ответе регистрации');
                setCheckingStatus(false);
                setIsReady(true);
                setShowSpinner(false);
                return;
            }

            const internalId = registerData.user.id;
            console.log('✅ Внутренний ID пользователя:', internalId);

            // Сохраняем internalUserId в state и localStorage
            setInternalUserId(internalId);
            localStorage.setItem('internalUserId', internalId.toString());

            // Для Telegram пользователей устанавливаем isAuthenticated в true
            setIsAuthenticated(true);

            // Проверяем блокировку
            console.log('🔍 Проверка блокировки...');
            const blockResponse = await fetch(`${API_URL}/api/user/${internalId}/is-blocked`);

            if (!blockResponse.ok) {
                console.error('❌ Ошибка проверки блокировки');
                setCheckingStatus(false);
                setIsReady(true);
                setShowSpinner(false);
                return;
            }

            const blockData = await blockResponse.json();
            setIsBlocked(blockData.isBlocked);
            console.log('🔒 Статус блокировки:', blockData.isBlocked ? 'Заблокирован' : 'Активен');

            // Загружаем ключи
            if (!blockData.isBlocked) {
                console.log('🔄 Загружаем ключи для internalId:', internalId);
                await fetchUserKeysDirect();
            }

        } catch (error) {
            console.error('❌ Ошибка при проверке блокировки:', error);
            setError('Ошибка соединения с сервером');
        } finally {
            setCheckingStatus(false);
            setIsReady(true);
            setShowSpinner(false);
        }
    };

    const handleCloseInsufficientBalance = () => {
        setShowInsufficientBalanceSheet(false);
        setInsufficientBalanceData({ required: 0, current: 0 });
    };

    const handleGoToWallet = () => {
        setShowInsufficientBalanceSheet(false);
        setInsufficientBalanceData({ required: 0, current: 0 });
        if (goWalletHandler) {
            goWalletHandler();
        }
    };

    const handleSpinnerComplete = () => { };

    const handleMenuChange = (item) => {
        if (item === activeMenuItem || isBlocked || maintenanceMode) return;

        setActiveMenuItem(item);
        setPageAnimation('exit');

        let targetPage = 'main';
        switch (item) {
            case 'vpn':
                targetPage = 'main';
                break;
            case 'wallet':
                targetPage = 'wallet';
                break;
            case 'ref':
                targetPage = 'referral';
                break;
            case 'profile':
                targetPage = 'profile';
                break;
            case 'faq':
                targetPage = 'faq';
                break;
            default:
                targetPage = 'main';
        }

        setNextPage(targetPage);
    };

    const handleAnimationEnd = () => {
        if (pageAnimation === 'exit' && nextPage) {
            setCurrentPage(nextPage);
            setPageAnimation('enter');
            setNextPage(null);
        }
    };

    const handleBuyClick = (data) => {
        if (isBlocked || maintenanceMode) return;

        if (data?.renewUuid) {
            setRenewData({ uuid: data.renewUuid });
        }
        setCurrentPage('purchase');
    };

    const handleViewKey = (key) => {
        if (isBlocked || maintenanceMode) return;

        setSelectedKey(key);
        setActiveTab(null);
        setCurrentPage('key-detail');
    };

    const handleChangeTariff = (key) => {
        if (isBlocked || maintenanceMode) return;

        setSelectedKey(key);
        setActiveTab('change');
        setCurrentPage('key-detail');
    };

    const handleBackToMain = () => {
        fetchUserKeysDirect();
        setCurrentPage('main');
        setRenewData(null);
        setSelectedKey(null);
    };

    const handleGoToKeys = () => {
        setShowSuccessSheet(false);

        if (newlyPurchasedKeyUuid) {
            const purchasedKey = userKeys.find(k =>
                k.uuid === newlyPurchasedKeyUuid ||
                k.key_number === newlyPurchasedKeyUuid
            );

            if (purchasedKey) {
                setSelectedKey(purchasedKey);
                setCurrentPage('key-detail');
            } else {
                setCurrentPage('main');
                fetchUserKeys();
            }

            setNewlyPurchasedKeyUuid(null);
        } else if (selectedKey) {
            setCurrentPage('key-detail');
        } else {
            setCurrentPage('main');
            fetchUserKeys();
        }
    };

    const handleRenewGoToKeys = () => {
        setShowSuccessRenewSheet(false);

        if (newlyPurchasedKeyUuid) {
            const purchasedKey = userKeys.find(k =>
                k.uuid === newlyPurchasedKeyUuid ||
                k.key_number === newlyPurchasedKeyUuid
            );

            if (purchasedKey) {
                setSelectedKey(purchasedKey);
                setCurrentPage('key-detail');
            } else {
                setCurrentPage('main');
                fetchUserKeys();
            }

            setNewlyPurchasedKeyUuid(null);
        } else if (selectedKey) {
            setCurrentPage('key-detail');
        } else {
            setCurrentPage('main');
            fetchUserKeys();
        }
    };

    const handleAfterRenew = async (renewedKeyUuid) => {
        console.log('🔄 handleAfterRenew вызван, renewedKeyUuid:', renewedKeyUuid);
        setShowLoadingSheet(true);

        try {
            const updatedKeys = await refreshUserData();
            setShowLoadingSheet(false);

            if (renewedKeyUuid && updatedKeys) {
                const renewedKey = updatedKeys.find(k => k.uuid === renewedKeyUuid);
                if (renewedKey) {
                    setSelectedKey(renewedKey);
                }
            }

            setShowSuccessRenewSheet(true);
        } catch (error) {
            console.error('❌ Ошибка в handleAfterRenew:', error);
            setShowLoadingSheet(false);
        }
    };

    const fetchUserKeysDirect = async (preferredInternalId = null) => {
        try {
            console.log('📡 Загрузка ключей...');
            const currentTelegramId = telegramId ||
                window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
            const resolvedInternalId = preferredInternalId || internalUserId ||
                await getInternalIdByTelegramId(currentTelegramId);
            if (!resolvedInternalId) throw new Error('Не удалось определить внутренний ID пользователя');
            const response = await fetch(`${API_URL}/api/user/${resolvedInternalId}/keys`, { cache: 'no-store' });
            const data = await response.json();
            if (data.success) {
                setUserKeys(data.keys);
                setSelectedKey(previous => previous
                    ? data.keys.find(key => key.uuid === previous.uuid) || previous
                    : previous
                );
                return data.keys;
            } else {
                setError(data.error || 'Ошибка загрузки ключей');
                return [];
            }
        } catch (error) {
            console.error('❌ Ошибка:', error);
            setError(error.message);
            return [];
        } finally {
            setMainPageDataLoaded(true);
            setTimeout(() => setShowSpinner(false), 500);
        }
    };

    const refreshUserData = async (changedKeyUuid = null) => {
        const keys = await fetchUserKeysDirect();
        const resolvedInternalId = internalUserId;

        if (resolvedInternalId) {
            try {
                const balanceResponse = await fetch(
                    `${API_URL}/api/user/${resolvedInternalId}/balance`,
                    { cache: 'no-store' }
                );
                const balanceData = await balanceResponse.json();
                if (balanceData.success) {
                    window.dispatchEvent(new CustomEvent('balance-updated', {
                        detail: { balance: balanceData.balance }
                    }));
                }
            } catch (error) {
                console.error('❌ Ошибка обновления баланса:', error);
            }
        }

        window.dispatchEvent(new CustomEvent('hydvpn-data-updated'));
        return changedKeyUuid ? keys.find(key => key.uuid === changedKeyUuid) || null : keys;
    };

    // Telegram может замораживать WebView, не размонтируя React-компоненты.
    // После возврата в приложение подтягиваем актуальные данные без перезапуска.
    useEffect(() => {
        const refreshWhenVisible = () => {
            if (document.visibilityState === 'visible' && internalUserId && isReady) {
                refreshUserData();
            }
        };

        document.addEventListener('visibilitychange', refreshWhenVisible);
        window.addEventListener('focus', refreshWhenVisible);
        return () => {
            document.removeEventListener('visibilitychange', refreshWhenVisible);
            window.removeEventListener('focus', refreshWhenVisible);
        };
    }, [internalUserId, isReady]);

    const handleSuccessSheetClose = () => {
        setShowSuccessSheet(false);
        setNewlyPurchasedKeyUuid(null);
    };

    const handleSuccessRenewSheetClose = () => {
        setShowSuccessRenewSheet(false);
        setNewlyPurchasedKeyUuid(null);
    };

    const handlePurchase = async (purchaseData) => {
        if (isBlocked || maintenanceMode) return;

        setShowLoadingSheet(true);

        try {
            if (!purchaseData?.renewUuid && !purchaseData?.ignoreWarning) {
                console.log('🔍 Проверка активных ключей...');
                const keysResponse = await fetch(`${API_URL}/api/user/keys?user_id=${internalUserId}`);
                const keysData = await keysResponse.json();
            }

            setPendingPurchase(purchaseData);

            const response = await fetch(`${API_URL}/api/purchase`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    internalUserId,
                    telegramId,
                    userId: internalUserId,
                    period: purchaseData.period,
                    whiteList: purchaseData.whiteList,
                    devices: purchaseData.devices,
                    userData: {
                        username: user?.username || webUser?.username,
                        first_name: user?.first_name || webUser?.first_name,
                        last_name: user?.last_name || webUser?.last_name,
                        language_code: user?.language_code || 'ru',
                        is_premium: user?.is_premium ? 1 : 0
                    }
                })
            });

            const responseData = await response.json();

            if (responseData.success) {
                setShowLoadingSheet(false);
                sendData({
                    period: purchaseData.period,
                    devices: purchaseData.devices,
                    whiteList: purchaseData.whiteList,
                    totalPrice: purchaseData.totalPrice,
                    remnawaveUuid: responseData.remnawave_uuid,
                    subscription_url: responseData.subscription_url
                });

                if (responseData.remnawave_uuid || responseData.newKey?.uuid) {
                    setNewlyPurchasedKeyUuid(responseData.remnawave_uuid || responseData.newKey?.uuid);
                }

                setPendingPurchase(purchaseData);
                setShowSuccessSheet(true);
                await refreshUserData(responseData.remnawave_uuid || responseData.newKey?.uuid);
            } else {
                setShowLoadingSheet(false);
                if (responseData.error === 'insufficient_balance') {
                    setInsufficientBalanceData({
                        required: responseData.required || purchaseData.totalPrice,
                        current: responseData.currentBalance || 0
                    });
                    setShowInsufficientBalanceSheet(true);
                } else {
                    alert('❌ Ошибка при оформлении: ' + (responseData.error || 'Неизвестная ошибка'));
                }
            }
        } catch (error) {
            console.error('❌ Ошибка при покупке:', error);
            alert(`❌ Сетевая ошибка. Проверьте подключение. ${error}`);
        } finally {
            setShowLoadingSheet(false);
        }
    };

    // Инициализация Telegram SDK
    useEffect(() => {
        console.log('🔧 Инициализация, isTelegram =', isTelegram);

        if (!isTelegram) {
            console.log('📱 Приложение запущено вне Telegram');
            setTelegramInitialized(true);
            setShowSpinner(false);
            setCurrentPageLoading(false);
            return;
        }

        const initializeTelegram = async () => {
            try {
                await init();
                console.log('✅ Telegram SDK инициализирован');

                if (miniApp.ready.isAvailable()) {
                    await miniApp.ready();
                    const platformTg = window.Telegram?.WebApp?.platform;
                    const isWindows = platformTg === 'windows' || platformTg === 'tdesktop';

                    if (!isWindows) {
                        window.Telegram.WebApp.requestFullscreen();
                    } else {
                        if (window.Telegram.WebApp.setHeaderColor) {
                            window.Telegram.WebApp.setHeaderColor('#080d13');
                            window.Telegram.WebApp.setBackgroundColor('#080d13');
                        }
                        if (window.Telegram.WebApp.expand) {
                            window.Telegram.WebApp.expand();
                        }
                    }

                    if (window.Telegram.WebApp.disableVerticalSwipes) {
                        window.Telegram.WebApp.disableVerticalSwipes();
                    }
                }

                backButton.mount();

                if (hapticFeedback && hapticFeedback.isAvailable) {
                    console.log('✅ Haptic feedback доступен');
                }

                setTelegramInitialized(true);
                setShowSpinner(false);
                setCurrentPageLoading(false);
            } catch (error) {
                console.error('❌ Ошибка инициализации Telegram SDK:', error);
                setTelegramInitialized(true);
                setShowSpinner(false);
                setCurrentPageLoading(false);
            }
        };

        initializeTelegram();
    }, [isTelegram]);

    // Добавьте этот useEffect после инициализации Telegram
    useEffect(() => {
        // Если это Telegram и нет internalUserId, но есть userId из хука
        if (isTelegram && userId && !internalUserId && !isAuthenticated) {
            console.log('🔄 Инициализация Telegram пользователя, userId:', userId);
            // Устанавливаем isAuthenticated в true для Telegram пользователей
            setIsAuthenticated(true);
            // Вызываем checkBlockStatus для регистрации/получения пользователя
            checkBlockStatus();
        }
    }, [isTelegram, userId, internalUserId, isAuthenticated]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('internalUserId');
        setIsAuthenticated(false);
        setWebUser(null);
        setInternalUserId(null);
        window.location.href = '/';
    };

    // Форсированная инициализация
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!telegramInitialized) {
                console.log('⚠️ Таймаут: принудительно завершаем инициализацию');
                setTelegramInitialized(true);
                setShowSpinner(false);
                setCurrentPageLoading(false);
            }
        }, 3000);

        return () => clearTimeout(timer);
    }, [telegramInitialized]);

    const goWalletHandler = () => {
        setCurrentPage('wallet');
        setActiveMenuItem("wallet");
    };

    const platformTg = window.Telegram?.WebApp?.platform;
    const isWindows = platformTg === 'windows' || platformTg === 'tdesktop';

    // Показываем спиннер
    if (!telegramInitialized || showSpinner || currentPageLoading) {
        return (
            <div className="app-container spinner-fullscreen">
                <PolygonSpinner onComplete={handleSpinnerComplete} />
                {error && <div className="error-message">{error}</div>}
            </div>
        );
    }

    // Telegram-only сборка: ни экран входа, ни служебные web-маршруты не
    // должны открываться без настоящих подписанных данных Mini App.
    if (!isTelegram) {
        return null;
    }

    // В App.jsx, перед основными проверками:
    const path = window.location.pathname;

// Сначала проверяем специальные страницы
    if (path === '/telegram-auth') {
        console.log('🔐 Рендерим TelegramAuthHandler');
        return <TelegramAuthHandler />;
    }

    if (path === '/verify-email') {
        return <VerifyEmailPage />;
    }

// В начале App.jsx, перед return, добавьте:
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
        console.log('🔐 Token found in URL at App level');
        // Не делаем ничего, AuthPage обработает
    }

// Если не авторизован — показываем страницу входа
//     if (!isAuthenticated && !isTelegram) {
//           return <AuthPage onAuthSuccess={handleAuthSuccess} />;
//       }

    if (isTelegram && !internalUserId && !isAuthenticated) {
        console.log('🔄 Telegram пользователь, получаем internalUserId...');
        // Не возвращаем null, а продолжаем - checkBlockStatus отработает
    }

    console.log('App state:', { isAuthenticated, isTelegram });

    // Техработы
    if (maintenanceMode && !isAdmin && !isCheckingAdmin) {
        return <MaintenanceScreen message={maintenanceMessage} />;
    }

    // Блокировка
    if (isBlocked) {
        return <BlockedScreen />;
    }



    // Основной интерфейс
    return (
        <div className="app-container" style={isWindows ? { padding: 0 } : { paddingTop: 'calc(58px + env(safe-area-inset-top))' }}>
            <img src="/images/Vector.svg" alt="" className="bg-vector" />

            {/* Кнопка выхода в шапке */}
            {!isBlocked && !maintenanceMode && isAuthenticated && !isTelegram && (
                <div style={{ position: 'fixed', top: '10px', right: '10px', zIndex: 1000 }}>
                    <button onClick={handleLogout} className="logout-button">
                        Выйти
                    </button>
                </div>
            )}

            <div className={`content-area page-${pageAnimation}`} onAnimationEnd={handleAnimationEnd}>
                {currentPage === 'main' && (
                    <MainPage
                        onBuyClick={handleBuyClick}
                        onViewKey={handleViewKey}
                        userKeys={userKeys}
                        loading={false}
                        onPageLoaded={() => handlePageDataLoaded('main')}
                        afterRenew={handleAfterRenew}
                        onKeysUpdate={refreshUserData}
                        onGoWallet={goWalletHandler}
                        onChangeTariff={handleChangeTariff}
                        userId={internalUserId}
                    />
                )}

                {currentPage === 'wallet' && (
                    <WalletPage
                        onPageLoaded={() => handlePageDataLoaded('wallet')}
                        internalUserId={internalUserId}
                    />
                )}

                {currentPage === 'faq' && (
                    <FaqPage
                        onPageLoaded={() => handlePageDataLoaded('faq')}
                    />
                )}

                {currentPage === 'referral' && (
                    <ReferralPage
                        onPageLoaded={() => handlePageDataLoaded('referral')}
                    />
                )}

                {currentPage === 'profile' && (
                    <ProfilePage
                        userId={internalUserId}
                        onPageLoaded={() => handlePageDataLoaded('profile')}
                        onLogout={handleLogout}
                    />
                )}

                {currentPage === 'purchase' && (
                    <PurchasePage
                        onBack={handleBackToMain}
                        userId={internalUserId}
                        userData={user || webUser}
                        onPurchase={handlePurchase}
                        renewData={renewData}
                        onGoWallet={goWalletHandler}
                    />
                )}

                {currentPage === 'key-detail' && (
                    <KeyDetailPage
                        onBack={handleBackToMain}
                        onTariffChange={() => setCurrentPage('tariff-change')}
                        keyData={selectedKey}
                        onGoWallet={goWalletHandler}
                        userId={internalUserId}
                        closemenu={handleCloseMenu}
                        openmenu={handleOpenMenu}
                        tab={activeTab}
                        onDataChanged={refreshUserData}
                    />
                )}
            </div>

            {!isBlocked && !maintenanceMode && showBottomMenu && (
                <BottomMenu
                    activeItem={activeMenuItem}
                    onItemClick={handleMenuChange}
                    showProfile={!isTelegram && isAuthenticated}
                />
            )}

            <div className="shtorkamain">
                <SuccessRenewSheet
                    isVisible={showSuccessRenewSheet}
                    onClose={handleSuccessRenewSheetClose}
                    onGoToKeys={handleRenewGoToKeys}
                    successData={selectedKey}
                />
            </div>

            <div className="shtorkamainpurchase">
                <SuccessSheet
                    isVisible={showSuccessSheet}
                    onClose={handleSuccessSheetClose}
                    onGoToKeys={handleGoToKeys}
                    successData={selectedKey}
                />
            </div>

            <LoadingSheet
                isVisible={showLoadingSheet}
                message="Операция выполняется"
            />

            <InsufficientBalanceSheet
                isVisible={showInsufficientBalanceSheet}
                onClose={handleCloseInsufficientBalance}
                requiredAmount={insufficientBalanceData.required}
                currentBalance={insufficientBalanceData.current}
                onGoWallet={handleGoToWallet}
            />
        </div>
    );
}

export default App;
